import React, { useCallback, useMemo } from 'react';
import { useTable, useExpanded } from 'react-table';
import { useState } from 'react';
import { Wrapper } from './styles';
import { columns, parseToFloatOrReturnZero } from './table-config';
import ModuleSubcomponent from '../ModuleSubcomponent';
import { getModuleRowBackgroundColor } from '../utils';
import EditableCell from '../../EditableCell';
import { orderBy } from 'lodash';

const Table = ({
  modules = [],
  projectName = '',
  integrationWeight,
  totalSiScore,
  totalSiScoreVendor,
  updateProjectField,
  updateModuleCellData,
  updateModule,
  onEditModule,
  onSaveModule,
  onScoringCompleted,
  onRejectScoring,
  isAdmin,
  projectPhase,
  projectPhaseName,
}) => {
  const memoModules = useMemo(() => modules, [modules]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, footerGroups, visibleColumns } = useTable(
    {
      columns: columns,
      data: memoModules,
      autoResetExpanded: false,
      onEditModule,
      isAdmin,
      updateCellData: updateModuleCellData,
      projectName,
      integrationWeight,
      totalSiScoreVendor,
      projectPhase,
    },
    useExpanded
  );

  const getRowStyles = useCallback(
    (row) => ({
      backgroundColor: getModuleRowBackgroundColor(row),
    }),
    []
  );

  return (
    <Wrapper>
      <table
        {...getTableProps()}
        className='table-module'>
        <thead className='thead-module'>
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className='tr-module'>
              {headerGroup.headers.map((column) => (
                <th
                  className='th-module'
                  {...column.getHeaderProps({
                    style: column.getCustomStyles && column.getCustomStyles(),
                  })}
                  rowSpan={`${column.rowSpan ?? 1}`}
                  colSpan={`${column.colSpan ?? column.getHeaderProps().colSpan}`}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className='tbody-module'>
          {rows.map((row, rowInd) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            const rowStyles = getRowStyles(row.original);
            return (
              <React.Fragment key={rowProps.key}>
                <tr
                  className='tr-module'
                  {...rowProps}
                  style={rowStyles}>
                  {row.cells.map((cell) => {
                    const columnStyles = cell.column.getCustomStyles && cell.column.getCustomStyles();
                    return (
                      <td
                        className='td-module'
                        {...cell.getCellProps({
                          style: {
                            minWidth: columnStyles?.minWidth,
                            width: columnStyles?.width,
                            maxWidth: columnStyles?.maxWidth,
                          },
                        })}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className='td-expanded-module'>
                      {
                        <ModuleSubcomponent
                          module={memoModules[rowInd]}
                          updateModule={updateModule}
                          onEditModule={() => onEditModule(row.original)}
                          onSaveModule={onSaveModule}
                          onScoringCompleted={onScoringCompleted}
                          onRejectScoring={onRejectScoring}
                          isAdmin={isAdmin}
                          projectPhase={projectPhase}
                          projectPhaseName={projectPhaseName}
                        />
                      }
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot className='tfoot-module'>
          {/* SPACING ROW */}
          <tr className='tr-module'>
            <td></td>
            <td className='td-module-invisible' colSpan={9}>&nbsp;</td>
          </tr>
          {/* INTEGRATION WEIGHT ROW */}
          <tr className='tr-module'>
            <td></td>
            <td className='td-module-invisible'></td>
            <td className='td-module'>Integration Weight:</td>
            <td
              className='td-module'
              style={{ width: '7%' }}>
              {isAdmin ? (
                <EditableCell
                  type='number'
                  value={integrationWeight}
                  updateCellData={(_, __, value) =>
                    updateProjectField('integrationWeight', parseToFloatOrReturnZero(value), true)
                  }
                />
              ) : (
                integrationWeight || 0
              )}
            </td>
            <td className='td-module-invisible'></td>
            <td className='td-module-invisible' colSpan={2}></td>
            <td className='td-module-invisible'></td>
            <td className='td-module-invisible'></td>
            <td className='td-module-invisible'></td>
          </tr>
          {/* TOTAL WEIGHT ROW */}
          {footerGroups.slice(0, 1).map((footerGroup) => (
            <tr
              className='tr-module'
              {...footerGroup.getFooterGroupProps()}>
              {footerGroup.headers.map((column) => {
                const colspan = column.id === 'referenceModuleName' ? 2 : 1;
                const classname = (column.id === 'name' ||
                    column.id === 'estimatedWeight')
                    ? 'td-module' : 'td-module-invisible';
                return (
                    column.id !== 'expectedMaturityLevel' ? <td
                    className={classname} colspan={colspan}>
                    {column.render('Footer')}
                  </td> : null
                );
              })}
            </tr>
          ))}
        </tfoot>
      </table>
    </Wrapper>
  );
};

export default Table;
